import type { ColGroupDef, ToolPanelDef, ToolPanelVisibleChangedEvent } from '@cmg/data-grid';
import { useDataGridContext } from '@cmg/data-grid';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo } from 'react';

import type { CalendarGridRowData } from '../components/calendar-grid/CalendarGrid.model';

const widthConfig = {
  width: 300,
  minWidth: 300,
  maxWidth: 300,
};

const columnsToolPanelDef: ToolPanelDef = {
  id: 'columns',
  labelDefault: '',
  labelKey: 'columns',
  iconKey: 'columnsTab',
  toolPanel: 'agColumnsToolPanel',
  toolPanelParams: {
    suppressValues: true,
    suppressPivotMode: true,
  },
  ...widthConfig,
};

const filtersToolPanelDef: ToolPanelDef = {
  id: 'filters',
  labelDefault: '',
  labelKey: 'filters',
  iconKey: 'filterTab',
  toolPanel: 'agFiltersToolPanel',
  ...widthConfig,
};

export type SideBarDefProps = Readonly<{
  filterLayout: ColGroupDef[] | undefined;
}>;
export const useSideBarDef = ({ filterLayout = [] }: SideBarDefProps) => {
  const { getOpenedToolPanel, addEventListener, removeEventListener } = useDataGridContext();

  const hasCustomFilterLayout = !isEmpty(filterLayout);
  const openedToolPanel = getOpenedToolPanel() ?? undefined;

  const handleToolPanelVisibleChanged = useCallback(
    (event: ToolPanelVisibleChangedEvent<CalendarGridRowData>) => {
      if (event.key === 'filters' && event.visible && hasCustomFilterLayout) {
        event.api.getToolPanelInstance('filters')?.setFilterLayout(filterLayout);
      }
    },
    [filterLayout, hasCustomFilterLayout]
  );

  useEffect(() => {
    addEventListener('toolPanelVisibleChanged', handleToolPanelVisibleChanged);

    return () => {
      removeEventListener('toolPanelVisibleChanged', handleToolPanelVisibleChanged);
    };
  }, [addEventListener, handleToolPanelVisibleChanged, removeEventListener]);

  return useMemo(
    () => ({
      toolPanels: [
        {
          ...filtersToolPanelDef,
          toolPanelParams: {
            suppressSyncLayoutWithGrid: hasCustomFilterLayout,
          },
        },
        columnsToolPanelDef,
      ],
      defaultToolPanel: openedToolPanel,
    }),
    [hasCustomFilterLayout, openedToolPanel]
  );
};
